import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Building2, X, Home, Landmark } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "../App";
import { toast } from "sonner";
import { api } from "../services/apiClient";
import { canAccessBrokerInventory } from "../constants/plans";
import { ACTIVATION_UI_COPY } from "../constants/uiCopy";
import { NAV_LABELS } from "../constants/uiCopy";

const MarketplaceInventory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [developments, setDevelopments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // all, properties, developments

  useEffect(() => {
    fetchMarketplace();
  }, []);

  const fetchMarketplace = async () => {
    try {
      const response = await api.get("/brokers/marketplace");

      let data = null;
      try {
        data = await response.json();
      } catch (_parseError) {
        data = null;
      }

      if (!response.ok) {
        const detail = String(data?.detail || data?.message || "").toLowerCase();
        const isEmptyInventoryResponse =
          response.status === 404 ||
          (detail.includes("no hay") &&
            (detail.includes("propiedad") || detail.includes("inventario") || detail.includes("disponible")));

        if (isEmptyInventoryResponse) {
          setProperties([]);
          setDevelopments([]);
          return;
        }

        throw new Error("Error al cargar");
      }

      // Handle new response format with properties and developments
      if (data.properties && data.developments) {
        setProperties(data.properties);
        setDevelopments(data.developments);
      } else {
        // Backward compatibility with old format (array of properties)
        setProperties(Array.isArray(data) ? data : []);
        setDevelopments([]);
      }
    } catch (error) {
      console.error("Error fetching marketplace:", error);
      toast.error("Error al cargar inventario");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item) => {
    if (item.item_type === "development") {
      toast.info(
        "La solicitud de representación aplica solo para propiedades. Revisa el detalle del desarrollo para más información.",
      );
      return;
    }

    // Verificar login
    if (!user) {
      toast.error("Debes iniciar sesión");
      navigate("/login");
      return;
    }

    // Solo brokers pueden solicitar representación
    if (user.role !== "broker") {
      toast.info(
        "Solo brokers pueden solicitar representación. Puedes seguir explorando el inventario.",
      );
      return;
    }

    // Verificar plan broker activo
    if (!canAccessBrokerInventory(user)) {
      toast.error(ACTIVATION_UI_COPY.brokerInventoryNeedsActive);
      navigate("/account?tab=subscription");
      return;
    }

    setSelectedItem(item);
    setTermsAccepted(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setTermsAccepted(false);
  };

  const requestRepresentation = async () => {
    if (!termsAccepted) {
      toast.error("Debes aceptar los términos y condiciones");
      return;
    }

    try {
      // Co-listing only applies to individual properties
      const path = `/representation/properties/${selectedItem.id}/request`;

      const response = await api.post(path, {
        commission_percentage: selectedItem.commission_percentage || 3.0,
        notes: null,
      });

      if (!response.ok) {
        let errorMsg = "Error al solicitar";
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          errorMsg = `Error ${response.status}`;
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();
      toast.success(
        `Solicitud enviada. Slots restantes: ${result.max_brokers - result.current_brokers - 1}`,
      );
      closeModal();
      fetchMarketplace();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Filter items
  const allItems = [
    ...properties.map((p) => ({ ...p, item_type: "property" })),
    ...developments.map((d) => ({ ...d, item_type: "development" })),
  ];

  const getFilteredItems = () => {
    let items = [];
    if (activeTab === "all") items = allItems;
    else if (activeTab === "properties")
      items = properties.map((p) => ({ ...p, item_type: "property" }));
    else if (activeTab === "developments")
      items = developments.map((d) => ({ ...d, item_type: "development" }));

    return items.filter(
      (item) =>
        (item.title || item.name || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (item.address || item.location || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    );
  };

  const filteredItems = getFilteredItems();

  return (
    <div className="min-h-screen bg-muted overflow-x-hidden">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1
            className="text-4xl font-bold mb-4"
            style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
          >
            {NAV_LABELS.collaborationInventory}
          </h1>
          <p className="text-muted-foreground">
            Propiedades disponibles para representar y desarrollos para
            referencia comercial
          </p>
        </div>

        {/* Nota informativa sobre co-listing */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-blue-600 text-xl">🤝</span>
            <div>
              <h3 className="font-semibold text-blue-800 mb-1">
                Representación de propiedades habilitada
              </h3>
              <div className="text-sm text-blue-700 space-y-1.5">
                <p>
                  1) La solicitud aplica a propiedades con representación
                  habilitada y slots disponibles.
                </p>
                <p>
                  2) Desarrollos solo se muestran para consulta/prospección (sin
                  solicitud directa).
                </p>
                <p>
                  3) El badge{" "}
                  <span className="bg-purple-100 text-purple-700 px-1 rounded">
                    🤝 Co-listing
                  </span>{" "}
                  significa que ya hay al menos un broker representando esa
                  propiedad.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveTab("all")}
          >
            Todos ({properties.length + developments.length})
          </Button>
          <Button
            variant={activeTab === "properties" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveTab("properties")}
            className="flex items-center gap-1"
          >
            <Home className="w-4 h-4" />
            Propiedades ({properties.length})
          </Button>
          <Button
            variant={activeTab === "developments" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveTab("developments")}
            className="flex items-center gap-1"
          >
            <Landmark className="w-4 h-4" />
            Desarrollos ({developments.length})
          </Button>
        </div>

        <div className="mb-4 flex flex-wrap gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
            🏠 Propiedad: puedes solicitar representar
          </span>
          <span className="px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
            🏗️ Desarrollo: solo consulta/prospección
          </span>
          <span className="px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 font-medium">
            🤝 Co-listing: ya existe al menos 1 broker activo
          </span>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <Input
              type="text"
              placeholder="Buscar por título o ubicación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              No hay{" "}
              {activeTab === "developments"
                ? "desarrollos"
                : activeTab === "properties"
                  ? "propiedades"
                  : "inventario"}{" "}
              disponible
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className={`bg-background border rounded-lg overflow-hidden ${item.is_colisting ? "border-purple-300 ring-1 ring-purple-200" : "border-border"}`}
              >
                {/* Badge de tipo */}
                <div className="relative">
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={
                        item.image_urls?.[0] ||
                        "https://images.pexels.com/photos/35193584/pexels-photo-35193584.jpeg"
                      }
                      alt={item.title || item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span
                    className={`absolute top-2 left-2 px-2 py-1 text-xs font-semibold rounded-full ${
                      item.item_type === "development"
                        ? "bg-purple-100 text-purple-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {item.item_type === "development"
                      ? "🏗️ Desarrollo"
                      : "🏠 Propiedad"}
                  </span>
                  {/* Badge de Co-listing */}
                  {item.is_colisting && (
                    <span className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded-full bg-purple-500 text-white">
                      🤝 Co-listing
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-lg mb-2 line-clamp-2">
                    {item.title || item.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3 flex items-start">
                    <MapPin className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">
                      {item.address || item.location}
                    </span>
                  </p>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xl font-bold">
                      {item.item_type === "development" ? "Desde " : ""}$
                      {(
                        item.price_mxn ||
                        item.price_from_mxn ||
                        0
                      ).toLocaleString()}{" "}
                      MXN
                    </p>
                  </div>

                  {item.item_type === "property" ? (
                    <>
                      <div className="flex items-center justify-between text-xs mb-2">
                        <span className="text-muted-foreground">
                          💰 Comisión negociable
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full ${
                            item.slots_available === (item.max_brokers || 3)
                              ? "bg-green-100 text-green-700"
                              : item.slots_available === 1
                                ? "bg-amber-100 text-amber-700"
                                : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {item.slots_available || item.max_brokers || 3}/
                          {item.max_brokers || 3} slots
                        </span>
                      </div>

                      {item.current_broker_count > 0 && (
                        <p className="text-xs text-purple-600 mb-3">
                          👥 {item.current_broker_count} broker
                          {item.current_broker_count > 1 ? "s" : ""} ya
                          representa
                          {item.current_broker_count > 1 ? "n" : ""} esta
                          propiedad
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground mb-3 bg-muted px-2 py-1.5 rounded">
                      Este desarrollo es informativo para prospección. La
                      solicitud de representación aplica únicamente para
                      propiedades.
                    </p>
                  )}

                  <Button
                    className="w-full"
                    onClick={() => openModal(item)}
                    disabled={item.item_type === "development"}
                  >
                    {item.item_type === "development"
                      ? "Representación no disponible"
                      : "Solicitar Representar"}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full mt-2"
                    onClick={() =>
                      navigate(
                        item.item_type === "development"
                          ? `/development/${item.id}`
                          : `/property/${item.id}`,
                      )
                    }
                  >
                    {item.item_type === "development"
                      ? "Ver Desarrollo"
                      : "Ver Propiedad"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />

      {/* Modal de Términos y Condiciones */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                Términos y Condiciones de Representación
              </h2>
              <button
                onClick={closeModal}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedItem?.item_type === "development"
                        ? "bg-purple-100 text-purple-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {selectedItem?.item_type === "development"
                      ? "🏗️ Desarrollo"
                      : "🏠 Propiedad"}
                  </span>
                </div>
                <h3 className="font-bold text-lg mb-2">
                  Propiedad seleccionada:
                </h3>
                <p className="text-sm">
                  {selectedItem?.title || selectedItem?.name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {selectedItem?.address || selectedItem?.location}
                </p>
                <p className="text-lg font-bold mt-2">
                  $
                  {(
                    selectedItem?.price_mxn ||
                    selectedItem?.price_from_mxn ||
                    0
                  ).toLocaleString()}{" "}
                  MXN
                </p>

                {/* Comisión destacada */}
                <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-800">
                      💰 Comisión acordada:
                    </span>
                    <span className="text-xl font-bold text-green-700">
                      {selectedItem?.commission_percentage || 3}%
                    </span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    🔒 Esta comisión queda fija una vez que el propietario
                    apruebe tu solicitud. No podrá ser modificada
                    posteriormente.
                  </p>
                </div>
              </div>

              <div className="space-y-4 text-sm mb-6">
                <h3 className="font-bold text-base">
                  Condiciones de Representación:
                </h3>

                {/* Info de slots disponibles */}
                {selectedItem?.is_colisting && (
                  <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
                    <p className="text-purple-700 font-medium">
                      🤝 Esta propiedad ya tiene{" "}
                      {selectedItem?.current_broker_count} broker(s)
                      representándola.
                    </p>
                    <p className="text-sm text-purple-600">
                      Slots disponibles: {selectedItem?.slots_available} de{" "}
                      {selectedItem?.max_brokers}
                    </p>
                  </div>
                )}

                <div className="bg-muted p-4 rounded-lg space-y-3">
                  <p className="font-medium">1. Solicitud y Aprobación</p>
                  <p className="text-muted-foreground">
                    Tu solicitud será enviada al propietario. La representación
                    solo será efectiva una vez que el propietario apruebe tu
                    solicitud.
                  </p>

                  <p className="font-medium">2. Sistema de Co-listing</p>
                  <p className="text-muted-foreground">
                    <strong>
                      Esta propiedad permite múltiples representantes.
                    </strong>{" "}
                    Otros brokers pueden también representar esta misma
                    propiedad. La comisión se otorga al broker que concrete la
                    venta con su cliente.
                  </p>

                  <p className="font-medium">3. Comisión Fija</p>
                  <p className="text-muted-foreground">
                    La comisión es del{" "}
                    <strong>{selectedItem?.commission_percentage || 3}%</strong>{" "}
                    del valor de la transacción.
                    <strong>Esta comisión queda bloqueada</strong> una vez que
                    ambas partes acepten, garantizando seguridad para ambos.
                  </p>

                  <p className="font-medium">4. Obligaciones del Broker</p>
                  <p className="text-muted-foreground">
                    Como representante, te comprometes a: promocionar la
                    propiedad de manera profesional, mantener comunicación
                    transparente, reportar actividad de clientes potenciales, y
                    actuar bajo las regulaciones inmobiliarias aplicables.
                  </p>

                  <p className="font-medium">5. Revocación</p>
                  <p className="text-muted-foreground">
                    El propietario puede revocar tu representación en cualquier
                    momento. Tú también puedes renunciar a la representación.
                    Las comisiones solo aplican para ventas cerradas durante tu
                    período activo.
                  </p>

                  <p className="font-medium">6. Límites</p>
                  <p className="text-muted-foreground">
                    Cada propiedad define su límite de representación.
                    Actualmente puede configurarse entre 1 y 10 brokers.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 mb-6 bg-accent/10 p-4 rounded-lg">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4"
                />
                <label
                  htmlFor="terms-checkbox"
                  className="text-sm cursor-pointer flex-1"
                >
                  He leído y acepto los términos y condiciones de
                  representación. Entiendo que la aprobación del propietario es
                  necesaria y que la comisión será definida por él.
                </label>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={requestRepresentation}
                  disabled={!termsAccepted}
                  className="flex-1"
                >
                  {termsAccepted
                    ? "Enviar Solicitud"
                    : "Acepta los términos primero"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceInventory;
